import React, { useEffect, useMemo, useState } from "react";
import { Form, Input, InputNumber, Modal, Radio, Select, Switch } from "antd";
import type { UploadFile } from "antd/es/upload/interface";
import { useTranslation } from "react-i18next";
import {
  buildServiceCategoryFormData,
  DEFAULT_SERVICE_CATEGORY_SECTION,
  EntPhoto,
  normalizeServiceCategory,
  SERVICE_CATEGORY_SECTION_OPTIONS,
  ServiceCategoryRequestDto,
  ServiceCategorySection,
  ServiceCategoryType,
} from "features/serviceCategory/model/serviceCategory";
import { PhotoUpload } from "shared/ui/PhotoUpload";
import ServiceCategoryParentSelect from "./ServiceCategoryParentSelect";
import ServiceCodesSelect from "features/service/ui/ServiceCodesSelect";

type Initial = Omit<Partial<ServiceCategoryRequestDto>, "section"> & {
  id?: number;
  /** В ответе бэка раздел может быть пустым — это `GENERAL`. */
  section?: ServiceCategorySection | null;
  photo?: EntPhoto | null;
};

type Props = {
  open: boolean;
  loading?: boolean;
  initial?: Initial | null;
  /** Раздел для новой категории (обычно — открытый сейчас на странице). */
  defaultSection?: ServiceCategorySection;
  onCancel: () => void;
  onSubmit: (formData: FormData) => void;
  maxFiles?: number;
};

const ServiceCategoryFormModal = ({
  open,
  loading,
  initial,
  defaultSection = DEFAULT_SERVICE_CATEGORY_SECTION,
  onCancel,
  onSubmit,
  maxFiles = 1,
}: Props) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<ServiceCategoryRequestDto>();

  const isEdit = !!initial?.id;

  const [existingPhotos, setExistingPhotos] = useState<EntPhoto[]>([]);
  const [deletedPhotoIds, setDeletedPhotoIds] = useState<string[]>([]);
  const [fileList, setFileList] = useState<UploadFile[]>([]);

  const type = Form.useWatch("type", form);
  const section = Form.useWatch("section", form);

  useEffect(() => {
    if (!open) return;

    setDeletedPhotoIds([]);
    setFileList([]);

    const initPhotos: EntPhoto[] = initial?.photo ? [initial.photo] : [];
    setExistingPhotos(initPhotos);

    const initServiceCodes = (initial?.serviceCodes ?? [])
      .filter(Boolean)
      .map((c: any) => String(c));

    form.setFieldsValue({
      id: initial?.id,
      nameRu: initial?.nameRu ?? "",
      nameKy: initial?.nameKy ?? "",
      serviceCodes: initServiceCodes,
      url: initial?.url ?? "",
      isActive: initial?.isActive ?? true,
      descriptionRu: initial?.descriptionRu ?? "",
      descriptionKy: initial?.descriptionKy ?? "",
      type: (initial?.type as ServiceCategoryType) ?? "CATEGORY",
      section: isEdit
        ? (initial?.section ?? DEFAULT_SERVICE_CATEGORY_SECTION)
        : defaultSection,
      parentId: initial?.parentId ?? null,
      order: initial?.order ?? null,
    } as any);
  }, [open, initial, isEdit, defaultSection, form]);

  const files: File[] = useMemo(() => {
    return fileList
      .map((f) => (f as any).originFileObj)
      .filter(Boolean) as File[];
  }, [fileList]);

  const handleOk = async () => {
    const raw = await form.validateFields().catch((err) => {
      const first = err?.errorFields?.[0];
      if (first?.name) form.scrollToField(first.name);
      return null;
    });
    if (!raw) return;

    if (!isEdit && existingPhotos.length === 0 && files.length === 0) {
      return;
    }

    const dto = normalizeServiceCategory(raw, initial?.id, deletedPhotoIds);
    const formData = buildServiceCategoryFormData(dto, files);

    onSubmit(formData);
  };

  return (
    <Modal
      open={open}
      title={isEdit ? t("handbook.table.edit") : t("handbook.table.create")}
      onCancel={onCancel}
      onOk={handleOk}
      confirmLoading={loading}
      okText={isEdit ? t("handbook.table.save") : t("handbook.table.create")}
      cancelText={t("handbook.table.cancel")}
      centered
      destroyOnClose
      width={640}
    >
      <Form form={form} layout="vertical">
        <Form.Item
          name="nameRu"
          label="Наименование (RU)"
          rules={[
            {
              required: true,
              whitespace: true,
              message: "Введите название на русском",
            },
            { max: 255, message: "Максимум 255 символов" },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="nameKy"
          label="Наименование (KY)"
          rules={[
            {
              required: true,
              whitespace: true,
              message: "Введите название на кыргызском",
            },
            { max: 255, message: "Максимум 255 символов" },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          name="type"
          label="Тип"
          rules={[{ required: true, message: "Выберите тип" }]}
        >
          <Select
            options={[
              { value: "CATEGORY", label: "CATEGORY" },
              { value: "FORM", label: "FORM" },
              { value: "URL", label: "URL" },
            ]}
          />
        </Form.Item>

        <Form.Item
          name="section"
          label="Раздел"
          rules={[{ required: true, message: "Выберите раздел" }]}
        >
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            options={SERVICE_CATEGORY_SECTION_OPTIONS}
            // Родитель из другого раздела больше не подходит.
            onChange={() => form.setFieldValue("parentId", null)}
          />
        </Form.Item>

        <Form.Item name="parentId" label="Родительская категория">
          <ServiceCategoryParentSelect
            excludeId={initial?.id}
            section={section}
          />
        </Form.Item>

        <Form.Item
          name="url"
          label="URL"
          rules={[{ required: true, message: "Введите URL" }]}
        >
          <Input placeholder="Путь к сервису" />
        </Form.Item>

        <Form.Item
          name="order"
          label="Порядок сортировки"
          tooltip="Чем меньше число, тем выше категория в списке. Пустое значение — в конец."
          rules={[
            {
              type: "number",
              min: 0,
              message: "Порядок не может быть отрицательным",
            },
          ]}
        >
          <InputNumber
            min={0}
            precision={0}
            style={{ width: "100%" }}
            placeholder="Например, 1"
          />
        </Form.Item>

        <Form.Item name="isActive" label="Активная?" valuePropName="checked">
          <Switch />
        </Form.Item>

        <Form.Item name="descriptionRu" label="Описание (RU)">
          <Input.TextArea rows={3} />
        </Form.Item>

        <Form.Item name="descriptionKy" label="Описание (KY)">
          <Input.TextArea rows={3} />
        </Form.Item>

        <Form.Item name="serviceCodes" label="Сервисы">
          <ServiceCodesSelect />
        </Form.Item>

        <Form.Item
          label="Фотографии"
          required
          rules={[
            {
              validator: async () => {
                if (
                  !isEdit &&
                  existingPhotos.length === 0 &&
                  files.length === 0
                ) {
                  throw new Error("Добавьте хотя бы 1 фото");
                }
              },
            },
          ]}
        >
          <PhotoUpload
            existingPhotos={existingPhotos}
            setExistingPhotos={setExistingPhotos}
            fileList={fileList}
            setFileList={setFileList}
            deletedPhotoIds={deletedPhotoIds}
            setDeletedPhotoIds={setDeletedPhotoIds}
            maxFiles={maxFiles}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ServiceCategoryFormModal;
